import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';
import { PRODUCTION_URL } from '../productionLink/productionLink';

const POSSetup = () => {
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showItemModal, setShowItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState({
    category_id: '',
    name: '',
    description: '',
    price: '',
    is_veg: true,
    preparation_time: 15
  });
  const [successMessage, setSuccessMessage] = useState('');

  const colors = {
    primary: '#2D5016',
    accent: '#6ba83e',
    lightGray: '#f8faf9',
    mediumGray: '#e8ede9',
    textDark: '#1a1a1a',
    textLight: '#6b7280',
    white: '#ffffff',
    success: '#4a7c2a',
    warning: '#f59e0b',
    danger: '#ef4444'
  };

  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([fetchMenuItems(), fetchCategories()]);
    setLoading(false);
  };

  const fetchMenuItems = async () => {
    try {
      const data = await apiGet('/admin/pos/setup/items');
      if (data.success) setMenuItems(data.data);
    } catch (error) {
      console.error('Error fetching menu items:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await apiGet('/admin/pos/setup/categories');
      if (data.success) setCategories(data.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSubmitItem = async (e) => {
    e.preventDefault();
    try {
      const url = editingItem
        ? `${PRODUCTION_URL}/api/admin/pos/setup/items/${editingItem.id}`
        : `${PRODUCTION_URL}/api/admin/pos/setup/items`;

      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(itemForm)
      });

      const data = await response.json();
      if (data.success) {
        setSuccessMessage(`✅ Item ${editingItem ? 'updated' : 'created'} successfully!`);
        setShowItemModal(false);
        setEditingItem(null);
        setItemForm({
          category_id: '',
          name: '',
          description: '',
          price: '',
          is_veg: true,
          preparation_time: 15
        });
        fetchAllData();
      }
    } catch (error) {
      console.error('Error saving item:', error);
    }
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setItemForm({
      category_id: item.category_id,
      name: item.name,
      description: item.description || '',
      price: item.price,
      is_veg: item.is_veg,
      preparation_time: item.preparation_time
    });
    setShowItemModal(true);
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      const response = await fetch(`${PRODUCTION_URL}/api/admin/pos/setup/items/${itemId}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        setSuccessMessage('✅ Item deleted successfully!');
        fetchAllData();
      }
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  const toggleAvailability = async (itemId, currentStatus) => {
    try {
      const response = await fetch(`${PRODUCTION_URL}/api/admin/pos/setup/items/${itemId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_available: !currentStatus })
      });

      const data = await response.json();
      if (data.success) {
        setSuccessMessage(`✅ Item ${!currentStatus ? 'enabled' : 'disabled'} successfully!`);
        fetchAllData();
      }
    } catch (error) {
      console.error('Error toggling availability:', error);
    }
  };

  const styles = {
    pageHeader: {
      marginBottom: isMobile ? '16px' : '24px',
      padding: isMobile ? '0 4px' : '0'
    },
    pageTitle: {
      fontSize: isMobile ? '20px' : isTablet ? '24px' : '28px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: '6px'
    },
    contentCard: {
      background: colors.white,
      padding: isMobile ? '16px' : '24px',
      borderRadius: isMobile ? '14px' : '16px',
      boxShadow: '0 8px 24px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`,
      margin: isMobile ? '0 4px 16px 4px' : '0 0 24px 0'
    },
    sectionTitle: {
      fontSize: isMobile ? '16px' : '20px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: isMobile ? '12px' : '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      justifyContent: 'space-between'
    },
    button: {
      padding: isMobile ? '8px 14px' : '10px 18px',
      borderRadius: '6px',
      border: 'none',
      fontSize: isMobile ? '12px' : '13px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: colors.primary,
      color: colors.white
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: isMobile ? '12px' : '13px'
    },
    tableHeader: {
      background: colors.lightGray,
      fontWeight: '700',
      textAlign: 'left',
      padding: isMobile ? '10px' : '12px',
      borderBottom: `2px solid ${colors.mediumGray}`
    },
    tableCell: {
      padding: isMobile ? '10px' : '12px',
      borderBottom: `1px solid ${colors.lightGray}`
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isMobile ? '20px' : '40px'
    },
    modalContent: {
      background: colors.white,
      borderRadius: isMobile ? '16px' : '20px',
      padding: isMobile ? '20px' : '32px',
      maxWidth: isMobile ? '100%' : '600px',
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto'
    },
    formGroup: {
      marginBottom: '16px'
    },
    label: {
      display: 'block',
      marginBottom: '6px',
      fontSize: '12px',
      fontWeight: '600',
      color: colors.textDark
    },
    input: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: '8px',
      border: `1px solid ${colors.mediumGray}`,
      fontSize: '13px',
      fontFamily: 'inherit'
    },
    select: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: '8px',
      border: `1px solid ${colors.mediumGray}`,
      fontSize: '13px',
      fontFamily: 'inherit',
      background: colors.white
    },
    actionButton: {
      padding: '6px 12px',
      borderRadius: '6px',
      border: 'none',
      fontSize: '11px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginRight: '6px'
    },
    successMessage: {
      padding: isMobile ? '12px 16px' : '14px 20px',
      borderRadius: '10px',
      marginBottom: '16px',
      fontSize: isMobile ? '13px' : '14px',
      fontWeight: '600',
      background: 'rgba(74, 124, 42, 0.15)',
      color: colors.success,
      border: `2px solid ${colors.success}`,
      margin: isMobile ? '0 4px 12px 4px' : '0 0 16px 0'
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <div>Loading setup...</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {successMessage && (
        <div style={styles.successMessage}>
          {successMessage}
        </div>
      )}

      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>POS Setup</h1>
        <p style={{ fontSize: isMobile ? '12px' : '14px', color: colors.textLight }}>
          {isMobile ? 'Items and menu setup' : 'Configure menu items, pricing, categories, and POS settings'}
        </p>
      </div>

      {/* Categories Summary */}
      <div style={styles.contentCard}>
        <h2 style={styles.sectionTitle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>📂</span>
            <span>Categories</span>
          </div>
        </h2>
        {categories.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '30px', color: colors.textLight }}>
            No categories found
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: '12px' }}>
            {categories.map(cat => (
              <div key={cat.id} style={{
                padding: isMobile ? '14px' : '16px',
                background: colors.lightGray,
                borderRadius: '10px',
                border: `1px solid ${colors.mediumGray}`
              }}>
                <div style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: '700', marginBottom: '6px', color: colors.primary }}>
                  {cat.name}
                </div>
                <div style={{ fontSize: '12px', color: colors.textLight }}>
                  {cat.items_count} item{cat.items_count !== 1 ? 's' : ''}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Menu Items */}
      <div style={styles.contentCard}>
        <div style={styles.sectionTitle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>🍽️</span>
            <span>Menu Items</span>
          </div>
          <button
            onClick={() => {
              setEditingItem(null);
              setItemForm({
                category_id: '',
                name: '',
                description: '',
                price: '',
                is_veg: true,
                preparation_time: 15
              });
              setShowItemModal(true);
            }}
            style={styles.button}
          >
            + Add Item
          </button>
        </div>

        {menuItems.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '30px', color: colors.textLight }}>
            No menu items found
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.tableHeader}>Name</th>
                  {!isMobile && <th style={styles.tableHeader}>Category</th>}
                  <th style={styles.tableHeader}>Price</th>
                  {!isMobile && <th style={styles.tableHeader}>Type</th>}
                  <th style={styles.tableHeader}>Status</th>
                  <th style={styles.tableHeader}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {menuItems.map(item => (
                  <tr key={item.id} style={{ opacity: item.is_available ? 1 : 0.6 }}>
                    <td style={styles.tableCell}>
                      <strong>{item.name}</strong>
                    </td>
                    {!isMobile && (
                      <td style={styles.tableCell}>{item.category_name}</td>
                    )}
                    <td style={styles.tableCell}>
                      <strong>₹{parseFloat(item.price).toFixed(2)}</strong>
                    </td>
                    {!isMobile && (
                      <td style={styles.tableCell}>
                        {item.is_veg ? (
                          <span style={{ color: colors.success, fontSize: '11px' }}>🟢 Veg</span>
                        ) : (
                          <span style={{ color: colors.danger, fontSize: '11px' }}>🔴 Non-Veg</span>
                        )}
                      </td>
                    )}
                    <td style={styles.tableCell}>
                      <button
                        onClick={() => toggleAvailability(item.id, item.is_available)}
                        style={{
                          ...styles.actionButton,
                          background: item.is_available ? colors.success : colors.danger,
                          color: colors.white
                        }}
                      >
                        {item.is_available ? '✓ Available' : '✕ Unavailable'}
                      </button>
                    </td>
                    <td style={styles.tableCell}>
                      <button
                        onClick={() => handleEditItem(item)}
                        style={{
                          ...styles.actionButton,
                          background: colors.accent,
                          color: colors.white
                        }}
                      >
                        ✏️ Edit
                      </button>
                      <button
                        onClick={() => handleDeleteItem(item.id)}
                        style={{
                          ...styles.actionButton,
                          background: colors.danger,
                          color: colors.white
                        }}
                      >
                        🗑️ Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Item Modal */}
      {showItemModal && (
        <div style={styles.modal} onClick={() => setShowItemModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontSize: isMobile ? '18px' : '22px', marginBottom: '20px', fontWeight: '700' }}>
              {editingItem ? 'Edit Item' : 'Add New Item'}
            </h3>

            <form onSubmit={handleSubmitItem}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Category *</label>
                <select
                  value={itemForm.category_id}
                  onChange={(e) => setItemForm({ ...itemForm, category_id: e.target.value })}
                  style={styles.select}
                  required
                >
                  <option value="">Select category...</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Item Name *</label>
                <input
                  type="text"
                  value={itemForm.name}
                  onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                  style={styles.input}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Description</label>
                <textarea
                  value={itemForm.description}
                  onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                  style={{ ...styles.input, minHeight: '80px' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Price (₹) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={itemForm.price}
                    onChange={(e) => setItemForm({ ...itemForm, price: e.target.value })}
                    style={styles.input}
                    required
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Prep Time (min)</label>
                  <input
                    type="number"
                    value={itemForm.preparation_time}
                    onChange={(e) => setItemForm({ ...itemForm, preparation_time: e.target.value })}
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Item Type *</label>
                <select
                  value={itemForm.is_veg}
                  onChange={(e) => setItemForm({ ...itemForm, is_veg: e.target.value === 'true' })}
                  style={styles.select}
                >
                  <option value="true">🟢 Vegetarian</option>
                  <option value="false">🔴 Non-Vegetarian</option>
                </select>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                <button type="submit" style={{ ...styles.button, flex: 1 }}>
                  {editingItem ? 'Update Item' : 'Add Item'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowItemModal(false)}
                  style={{ ...styles.button, background: colors.mediumGray, color: colors.textDark, flex: 1 }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default POSSetup;